/**
 * Mapeamento do locale do app (pt/en/es…, BCP-47 curto do Módulo de Idiomas) para um
 * locale BCP-47 ADEQUADO À FORMATAÇÃO DE DATAS (`Intl.DateTimeFormat`).
 *
 * Espelha `numberLocale.ts`, mas para DATAS DE CONTEÚDO EDITORIAL (meses por extenso, dia/ano).
 * Diferença de produto: as datas editoriais SEMPRE seguem o leitor (sem gate), ao contrário dos
 * números cripto (que ficam fixos em pt-BR — ver `useNumberLocale.ts`).
 *
 * REGRA: aqui só muda o FORMATO/locale da data — nunca o conteúdo. Com o Módulo de Idiomas
 * DESLIGADO, o app fica em `pt` → cai no default `pt-BR` (retrocompat total).
 */

/** Locale BCP-47 de data usado quando nada combina (mantém comportamento histórico do site). */
export const DEFAULT_DATE_LOCALE = 'pt-BR'

/**
 * Tabela explícita app-locale → BCP-47 de data. Espelha o superset do Payload (`catalog.ts`
 * `LANGUAGE_SCHEMA_SUPERSET`). Para códigos já regionais (ex.: `zh-CN`) o próprio código serve.
 */
export const DATE_LOCALE_BY_APP_LOCALE: Record<string, string> = {
  pt: 'pt-BR',
  en: 'en-US',
  es: 'es-ES',
  it: 'it-IT',
  fr: 'fr-FR',
  de: 'de-DE',
  tr: 'tr-TR',
  nl: 'nl-NL',
  ko: 'ko-KR',
  pl: 'pl-PL',
  id: 'id-ID',
  ja: 'ja-JP',
  'zh-cn': 'zh-CN',
  ar: 'ar',
  ru: 'ru-RU',
  hi: 'hi-IN',
  th: 'th-TH',
  vi: 'vi-VN',
}

/**
 * Resolve o BCP-47 de data a partir do locale do app.
 * - `null`/`undefined`/vazio → default (`pt-BR`).
 * - Código conhecido → da tabela.
 * - Código desconhecido mas válido p/ `Intl` → usado como está.
 * - Inválido → default.
 */
export function resolveDateLocale(appLocale: string | null | undefined): string {
  const raw = appLocale?.trim()
  if (!raw) return DEFAULT_DATE_LOCALE

  const mapped = DATE_LOCALE_BY_APP_LOCALE[raw.toLowerCase()]
  if (mapped) return mapped

  // Código fora da tabela: aceita se o Intl o reconhecer; senão volta ao default.
  try {
    if (Intl.DateTimeFormat.supportedLocalesOf([raw]).length > 0) return raw
  } catch {
    /* BCP-47 malformado → default */
  }
  return DEFAULT_DATE_LOCALE
}

/**
 * Formata uma data de conteúdo editorial conforme o locale do leitor.
 * Estilo padrão por extenso (`15 de junho de 2026` / `June 15, 2026`); passe `opts` p/ outro estilo.
 */
export function formatEditorialDate(
  date: Date | string | number,
  appLocale?: string | null,
  opts?: Intl.DateTimeFormatOptions,
): string {
  // Guarda contra data malformada — Intl.format(Invalid Date) lança RangeError; devolve '' como
  // o caminho histórico (toLocaleDateString não quebrava o render).
  const d = new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat(
    resolveDateLocale(appLocale),
    opts ?? { day: 'numeric', month: 'long', year: 'numeric' },
  ).format(d)
}
